export const CORE_LOOP_GROUPS = [
  {
    number: '1',
    title: 'Orient & Observe',
    shortTitle: 'Orient',
    detail: 'Name what matters and capture what is happening.',
    icon: 'compass'
  },
  {
    number: '2',
    title: 'Choose & Test',
    shortTitle: 'Test',
    detail: 'Choose a useful outcome and try one small action.',
    icon: 'flask-conical'
  },
  {
    number: '3',
    title: 'Notice & Reflect',
    shortTitle: 'Reflect',
    detail: 'Collect signals and compare them with your starting point.',
    icon: 'search'
  },
  {
    number: '4',
    title: 'Decide & Integrate',
    shortTitle: 'Decide',
    detail: 'Keep, adjust, pause, or stop, then carry the learning forward.',
    icon: 'check-circle'
  }
];

export const ONBOARDING_START_GUIDANCE = Object.freeze({
  title: 'Choose one starting area',
  summary: 'Your first-run choice preselects the category for a new Observation or Experiment on that device.',
  detail: 'The starting area is a device-local default, not a permanent label. Change the category in either form or explore any other area at any time.'
});

export const AI_COACH_CONTEXT_GUIDANCE = Object.freeze({
  guidedReview: 'Review context opens the relevant permission controls and returns you to the same Guided setup. Approve once for the exact provider and category list, then start the session without choosing those categories again.',
  contextBudget: 'Estimated input context updates from the current conversation, draft, selected records, workflow safeguards, and attachments. Start fresh keeps the current conversation in History and opens a clean conversation with new context approval. Select any previous conversation in History to review it or continue from it; switching conversations clears temporary Guided state and asks for context approval for the selected thread.',
  guidedFlexibility: 'Open Coach and Guided Coaching follow your latest request, including a new subject you have never discussed or logged. Coach adapts its perspective and depth, uses relevant general knowledge, and gives useful support before any professional boundary reminder. Ask it to answer, clarify, explain, correct course, or pause; Guided Coaching then continues the named outcome without treating that request as an answer to its previous question.',
  providerFailure: 'If a response fails, Coach keeps the message and gives guidance for the active provider type. Cloud runs never show Ollama or local-server instructions; local guidance names Ollama only when Ollama is the configured provider.'
});

export const AI_COACH_PATHS = [
  {
    title: 'Open Coach',
    detail: 'Start with your own words and get practical whole-person guidance.',
    icon: 'message-square'
  },
  {
    title: 'Guided Coaching',
    detail: 'Use Clarity When Overwhelmed or Difficult Decision for a bounded session.',
    icon: 'route'
  }
];

export const PLATFORM_GUIDANCE = [
  {
    id: 'web',
    title: 'Web',
    eyebrow: 'Across browsers',
    icon: 'globe',
    summary: 'PocketBase is the source of truth. Personal records sync as encrypted payloads.',
    action: 'Remember the sync key only on a browser profile you control.'
  },
  {
    id: 'mobile-web',
    title: 'Mobile web',
    eyebrow: 'Phone and tablet',
    icon: 'smartphone',
    summary: 'Use the responsive Web app with the same account, encryption, plans, and Coach controls.',
    action: 'Keep one trusted device working while moving to a new browser or phone.'
  },
  {
    id: 'desktop',
    title: 'Desktop Lifetime',
    eyebrow: 'Local-first',
    icon: 'laptop',
    summary: 'Work offline with local storage, local AI options, and explicit dataset-by-dataset sync.',
    action: 'Use Sync now and check queue health before reinstalling or removing local data.'
  }
];

export const HELP_TASKS = [
  {
    id: 'first-loop',
    title: 'Start my first loop',
    summary: 'Choose what matters, record a starting point, and run one small test.',
    icon: 'sprout',
    publicSection: 'use-the-loop',
    inAppSection: 'overview',
    keywords: ['start', 'loop', 'observe', 'experiment', 'test', 'reflect', 'decide']
  },
  {
    id: 'sync-recovery',
    title: 'Protect and sync my data',
    summary: 'Check encrypted sync, remembered keys, recovery limits, and device changes.',
    icon: 'shield-check',
    publicSection: 'safe-web-desktop-setup',
    inAppSection: 'safesetup',
    keywords: ['sync', 'key', 'password', 'recovery', 'encryption', 'browser', 'device']
  },
  {
    id: 'desktop-setup',
    title: 'Set up Desktop Lifetime',
    summary: 'Understand local-first storage, offline use, local AI, and manual sync.',
    icon: 'laptop',
    publicSection: 'safe-web-desktop-setup',
    inAppSection: 'cloudsync',
    keywords: ['desktop', 'lifetime', 'offline', 'local', 'manual sync', 'dmg']
  },
  {
    id: 'ai-coach',
    title: 'Use AI Coach',
    summary: 'Choose Open Coach or Guided Coaching and control the context each conversation uses.',
    icon: 'bot',
    publicSection: 'ai-coach',
    inAppSection: 'aicoach',
    keywords: ['ai', 'coach', 'open coach', 'guided coaching', 'context', 'approval']
  },
  {
    id: 'plans',
    title: 'Check plans and billing',
    summary: 'Compare current access and find subscription or purchase controls.',
    icon: 'credit-card',
    publicSection: 'plans',
    inAppSection: 'buying',
    keywords: ['plan', 'billing', 'explorer', 'believer', 'guardian', 'lifetime', 'refund']
  },
  {
    id: 'data',
    title: 'Import, export, or back up',
    summary: 'Bring in journal entries, download your data, and prepare for a device change.',
    icon: 'database',
    publicSection: 'sync-privacy',
    inAppSection: 'datamanagement',
    keywords: ['import', 'export', 'backup', 'journal', 'data']
  },
  {
    id: 'support',
    title: 'Fix a problem or contact us',
    summary: 'Find recovery steps or email hello@traduotech.com for human help.',
    icon: 'life-buoy',
    publicSection: 'contact-support',
    inAppSection: 'safesetup',
    keywords: ['help', 'support', 'problem', 'error', 'contact', 'email']
  }
];

export const IN_APP_HELP_SECTIONS = [
  { id: 'overview', label: 'Overview', icon: 'sparkles', keywords: ['start', 'loop', 'first steps'] },
  { id: 'concepts', label: 'Key Concepts', icon: 'lightbulb', keywords: ['observe', 'experiment', 'reflect', 'decide'] },
  { id: 'features', label: 'Core Features', icon: 'gift', keywords: ['journal', 'think', 'timeline', 'results'] },
  { id: 'indicators', label: 'Signals', icon: 'bar-chart-3', keywords: ['energy', 'emotion', 'evidence', 'signal'] },
  { id: 'safesetup', label: 'Safe Setup', icon: 'key', keywords: ['password', 'remembered key', 'device', 'recovery'] },
  { id: 'cloudsync', label: 'Sync & Privacy', icon: 'shield-check', keywords: ['sync', 'encryption', 'desktop', 'queue'] },
  { id: 'datamanagement', label: 'Backups', icon: 'database', keywords: ['export', 'import', 'backup', 'restore'] },
  { id: 'globalsearch', label: 'Search', icon: 'search', keywords: ['find', 'command', 'navigation'] },
  { id: 'communitynetwork', label: 'Community', icon: 'users', keywords: ['network', 'profile', 'peer'] },
  { id: 'aicoach', label: 'AI Coach', icon: 'bot', keywords: ['open coach', 'guided coaching', 'context', 'local model'] },
  { id: 'buying', label: 'Plans', icon: 'credit-card', keywords: ['billing', 'explorer', 'believer', 'guardian', 'lifetime'] }
];

export const HELP_TRUST_FACTS = {
  manualPath: 'The complete manual loop remains available when AI is off or unavailable.',
  encryption: 'Personal records are encrypted on your device before cloud sync.',
  recovery: 'If every browser loses the old key and the previous password is unknown, the server cannot recover that key. The encrypted records remain stored but cannot be opened.',
  coachControl: 'AI Coach uses only the context categories you approve. It cannot change records without a separate explicit approval.',
  supportEmail: 'hello@traduotech.com'
};
